import { useRouter } from "next/router";
import { createContext, ReactNode, useContext } from "react";

import useContentMetadataGet from "@/content/useContentMetadataGet";
import useLangstonContentGet from "@/content/useLangstonContentGet";
import ResponseError from "@/errors/ResponseError";
import { UseRestQueryResponse } from "@/remoteState/useRestQuery/useRestQuery";
import { LangstonContentResponse } from "@/types/models/Content.interface";
import { Metadata } from "@/view/ContentHead";

type ViewPageContentContextType = {
  content: LangstonContentResponse | null | undefined;
  contentRequestError: ResponseError | undefined;
  isContentLoading: boolean;
  contentMetadata: Metadata[] | null | undefined;
  contentMetadataRequestError: typeof ResponseError | undefined;
  contentMetadataLoading: boolean;
};

const ViewPageContentContext = createContext<
  ViewPageContentContextType | undefined
>(undefined);

export const ViewPageContentContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Router
  const router = useRouter();
  const { query } = router;
  const { contentId } = query as {
    contentId: string;
  };

  // Fetching content/story data
  const {
    data: content,
    error,
    loading: isContentLoading,
  }: UseRestQueryResponse<LangstonContentResponse> = useLangstonContentGet<LangstonContentResponse>(
    contentId,
  );

  // Fetching content metadata
  const {
    data: contentMetadata,
    error: contentMetadataRequestError,
    loading: contentMetadataLoading,
  } = useContentMetadataGet(contentId);

  // Ensure error is an instance of ResponseError or undefined
  const contentRequestError: ResponseError | undefined =
    error instanceof ResponseError ? error : undefined;

  return (
    <ViewPageContentContext.Provider
      value={{
        content,
        contentRequestError,
        isContentLoading,
        contentMetadata,
        contentMetadataRequestError,
        contentMetadataLoading,
      }}
    >
      {children}
    </ViewPageContentContext.Provider>
  );
};

const useViewPageContentContext = () => {
  const viewPageContentContext = useContext(ViewPageContentContext);

  if (viewPageContentContext === undefined) {
    throw new Error(
      "ViewPageContentContext must be used within a ViewPageContentContextProvider.",
    );
  }

  return viewPageContentContext;
};

export default useViewPageContentContext;
